/**
 * Client Onboarding — config envelope (v1) in code.
 * Mirrors saas-typescript/docs/onboarding-config-v1.jsonc. Single source of the
 * builder's shape: blank/default factory, per-user-type option-map helpers, and
 * the assemble-to-payload for POST /admin/company/add.
 *
 * Conventions (verified against live company_profile rows):
 *   OPTION-MAP: { OptionName: { taken: bool, users: [userType, ...] } }
 *   FLAG-BAG:   addon_features booleans
 */

export const CONFIG_VERSION = 1;

// Fixed program catalog (step 4) — hardcoded per product decision.
export const PROGRAM_CATALOG = [
  "Rewardify", "DWAN", "Genuinity", "ScanAndWin", "SupplyBeam", "HybridOcean",
];

const optionMap = (names) =>
  names.reduce((acc, n) => ({ ...acc, [n]: { taken: false, users: [] } }), {});

// All-on messaging defaults (observed identical across tenants).
const MESSAGE_OPTIONS_DEFAULT = {
  otp: true, reset: true, set_mpin: true, registraion: true, bonus_points: true,
  extra_points: true, block_account: true, gift_delivery: true, redeem_request: true,
  birthday_wishes: true, activate_account: true, complaint_message: true,
  profile_completion: true, scan_coupon_points: true, redemption_reminders: true,
};
const MAIL_OPTIONS_DEFAULT = {
  claim_mail: true, points_mail: true, support_mail: true, welcome_mail: true,
  warranty_mail: true, redemption_mail: true, gift_status_mail: true, special_day_mail: true,
  celebrations_mail: true, registration_mail: true, account_status_mail: true,
  reset_password_mail: true, gift_redemption_mail: true, account_activation_mail: true,
};
const ADDON_FEATURES_DEFAULT = {
  scheme: false, campaign: false, referral: false, qr_rescan: false, dream_gift: false,
  kafka_scan: true, membership: false, bonus_system: false, point_expire: false,
  tds_deducted: false, loyalty_rules: false, sales_booster: false, scanning_type: "main",
  point_tracking: false, scratch_coupon: false, tds_compliance: false, instant_payment: false,
  multi_pop_on_qr: false, tds_calculation: false, aadhar_gender_dob: false,
  gift_auto_approval: false, loyalty_rules_type: "main", registration_bonus: false,
  kyc_online_verification: false,
};

/** A fresh, empty onboarding config. */
export const blankConfig = () => ({
  version: CONFIG_VERSION,

  company: {
    name: "", gstin: "", email: "", mobile: "", website: "",
    industry_id: 0, industry_name: "",
    theme_name: "", password: "", rePassword: "",
    thumbnailLogo: null, navbarLogo: null,
    address: { homeAddress: "", landMark: "", city: "", district: "", state: "", country: "", pincode: "" },
    socials: { facebook: "", instagram: "", twitter: "", youtube: "", linkedIn: "", whatsapp: "", portal: "" },
    contact_person_name: "", contact_person_number: "",
    customer_support_email: "", customer_support_mobile: "",
  },

  user_types: [],

  registration_options: optionMap(["Registration"]),
  login_options: optionMap(["Otp", "Password"]),
  approval_flow_options: optionMap(["Manual", "AutoApproval"]),
  product_availing: optionMap(PROGRAM_CATALOG),

  scanning: { scanning_type: "main", qr_type: "0", qr_prefix: "", qr_settings: null },

  points_sharing: {
    flat_points: true, percentage_points: false, percentage_points_value: null, percentage_on: null,
    is_point_reserved: false, point_reserved_percentage: null,
    is_point_sharing: false, is_point_sharing_bw_user: false,
    point_sharing_bw_user: { user: {}, percentage: {} },
  },

  // loyalty_rules_type is always inferred from scanning mode (read-only), not stored here
  features: { multi_pop_on_qr: false, dream_gift: false, registration_bonus: false, loyalty_rules: false, scheme: false },
  redemption_hierarchy: { is_mapped_users: false, gift_mapped_users: {}, gift_extra_approvals: {} },

  defaults: {
    status: "1", panel_status: "0", language: [],
    message_options: MESSAGE_OPTIONS_DEFAULT,
    mail_options: MAIL_OPTIONS_DEFAULT,
    addon_features: ADDON_FEATURES_DEFAULT,
    reserve_tds_points: {
      required_documents: { pan: false, gstin: false, aadhar: false },
      tds_reserve_percentage: { after_document_submission: 0, before_document_submission: 0 },
    },
    user_management: {},
  },
});

/** Toggle a user_type inside one option of an option-map (immutable). */
export const setOptionUser = (map, option, userType, on) => {
  const cur = map[option] || { taken: false, users: [] };
  const users = on ? [...new Set([...cur.users, userType])] : cur.users.filter((u) => u !== userType);
  return { ...map, [option]: { ...cur, taken: users.length > 0, users } };
};

/** Drop a user_type from every option of a map (used when a user_type is removed). */
export const pruneUserFromMap = (map, userType) =>
  Object.fromEntries(
    Object.entries(map).map(([opt, v]) => {
      const users = (v.users || []).filter((u) => u !== userType);
      return [opt, { ...v, taken: users.length > 0, users }];
    }),
  );

/**
 * Assemble the flat POST body for /admin/company/add.
 * - address/socials/user_types/language are JSON.parse'd server-side → stringify here.
 * - jsonb blobs pass through as objects.
 * - scheme mirrors are enforced from scanning_type so the two places can't drift.
 */
export const assemblePayload = (config) => {
  const c = config.company;

  const addon_features = {
    ...config.defaults.addon_features,
    multi_pop_on_qr: config.features.multi_pop_on_qr,
    dream_gift: config.features.dream_gift,
    registration_bonus: config.features.registration_bonus,
    loyalty_rules: config.features.loyalty_rules,
    scanning_type: config.scanning.scanning_type,
  };
  addon_features.scheme = addon_features.scanning_type === "scheme";
  // loyalty rules type is always inferred from the scanning mode
  if (addon_features.loyalty_rules) addon_features.loyalty_rules_type = addon_features.scanning_type;
  addon_features.disable_scheme_wallet = false;
  addon_features.disable_scheme_entries = false;

  return {
    name: c.name,
    email: c.email,
    password: c.password,
    mobile: c.mobile,
    website: c.website,
    gstin: c.gstin,
    theme_name: c.theme_name,
    contact_person_name: c.contact_person_name,
    contact_person_number: c.contact_person_number,
    customer_support_email: c.customer_support_email,
    customer_support_mobile: c.customer_support_mobile,
    industry_id: c.industry_id,
    industry_name: c.industry_name,
    logo: [c.thumbnailLogo, c.navbarLogo],
    qr_type: config.scanning.qr_type,
    qr_prefix: config.scanning.qr_prefix || null,
    qr_settings: config.scanning.qr_settings || null,
    status: config.defaults.status,
    panel_status: config.defaults.panel_status,
    // JSON.parse'd server-side:
    user_types: JSON.stringify(config.user_types),
    address: JSON.stringify(c.address),
    socials: JSON.stringify(c.socials),
    language: JSON.stringify(config.defaults.language),
    // jsonb pass-through:
    registration_options: config.registration_options,
    login_options: config.login_options,
    approval_flow_options: config.approval_flow_options,
    product_availing: config.product_availing,
    points_sharing: config.points_sharing,
    redemption_hierarchy: config.redemption_hierarchy,
    addon_features,
    message_options: config.defaults.message_options,
    mail_options: config.defaults.mail_options,
    reserve_tds_points: config.defaults.reserve_tds_points,
    user_management: config.defaults.user_management,
  };
};

/** Per-step required-field validation. Returns {field: message}. Empty = valid. */
export const validateStep = (config, stepIndex) => {
  const e = {};
  const c = config.company;
  switch (stepIndex) {
    case 0:
      if (!c.name.trim()) e.name = "Company name is required";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) e.email = "Enter a valid email";
      if (c.password.length < 8) e.password = "Password must be at least 8 characters";
      if (c.rePassword !== c.password) e.rePassword = "Passwords do not match";
      if (!/^\d{10}$/.test(c.mobile)) e.mobile = "Enter a valid 10-digit mobile";
      if (!c.gstin || c.gstin.trim().length < 15) e.gstin = "GSTIN must be 15 characters";
      if (!c.industry_id || Number(c.industry_id) === 0) e.industry_id = "Select an industry";
      if (!c.thumbnailLogo || !c.navbarLogo) e.logo = "Both logos are required";
      break;
    case 1:
      if (!config.user_types.length) e.user_types = "Add at least one user type";
      break;
    default:
      break;
  }
  return e;
};

/** User types that have no option selected in the given option-map key. */
export const typesMissing = (config, mapKey) =>
  config.user_types.filter((u) => !Object.values(config[mapKey]).some((o) => o.users.includes(u)));

/** User types enrolled in at least one option of the map. */
const typesCovered = (config, mapKey) =>
  config.user_types.filter((u) => Object.values(config[mapKey]).some((o) => o.users.includes(u)));

/**
 * Live completion status per step: "complete" | "attention" | "todo", with a
 * one-line hint. Drives the checklist + feedback banners. Index-aligned to the
 * wizard STEPS (0..6).
 */
export const getStepStatus = (cfg) => {
  const ut = cfg.user_types;
  const n = ut.length;

  // 0 — Company
  const companyErrs = Object.keys(validateStep(cfg, 0)).length;
  const company = companyErrs === 0
    ? { status: "complete", hint: "All required details provided" }
    : (cfg.company.name || cfg.company.email)
      ? { status: "attention", hint: `${companyErrs} field${companyErrs > 1 ? "s" : ""} need attention` }
      : { status: "todo", hint: "Not started" };

  // 1 — User types
  const userTypes = n
    ? { status: "complete", hint: `${n} user type${n > 1 ? "s" : ""}` }
    : { status: "todo", hint: "Add at least one" };

  // 2 — Access & flows (login + approval required per type)
  const loginMiss = typesMissing(cfg, "login_options").length;
  const apprMiss = typesMissing(cfg, "approval_flow_options").length;
  const access = !n
    ? { status: "todo", hint: "Needs user types" }
    : loginMiss === 0 && apprMiss === 0
      ? { status: "complete", hint: "Login & approval set for all" }
      : { status: "attention", hint: `${Math.max(loginMiss, apprMiss)} type(s) missing login/approval` };

  // 3 — Programs (enroll each type in ≥1 program)
  const enrolled = n ? typesCovered(cfg, "product_availing").length : 0;
  const programs = !n
    ? { status: "todo", hint: "Needs user types" }
    : enrolled === 0
      ? { status: "todo", hint: "No enrollments yet" }
      : enrolled === n
        ? { status: "complete", hint: "All types enrolled" }
        : { status: "attention", hint: `${n - enrolled} type(s) not enrolled` };

  // 4 — Scanning & points
  const ps = cfg.points_sharing;
  const pointsOk = ps.flat_points || (ps.percentage_points && ps.percentage_points_value && ps.percentage_on);
  const scanning = pointsOk
    ? { status: "complete", hint: `${cfg.scanning.scanning_type.toUpperCase()} · point logic set` }
    : { status: "attention", hint: "Choose a point calculation" };

  // 5 — Features (all optional)
  const enabledCount = Object.values(cfg.features).filter((v) => v === true).length;
  const features = { status: "complete", hint: enabledCount ? `${enabledCount} enabled` : "Defaults (none enabled)" };

  // 6 — Review
  const allErrs = validateAll(cfg).length;
  const review = !n
    ? { status: "todo", hint: "Complete earlier steps" }
    : allErrs === 0
      ? { status: "complete", hint: "Ready to register" }
      : { status: "attention", hint: `${allErrs} issue${allErrs > 1 ? "s" : ""} to resolve` };

  return [company, userTypes, access, programs, scanning, features, review];
};

/** Validate the whole config for the final gate. Returns array of messages. */
export const validateAll = (config) => {
  const msgs = [];
  const s0 = validateStep(config, 0);
  const s1 = validateStep(config, 1);
  [...Object.values(s0), ...Object.values(s1)].forEach((m) => msgs.push(m));
  // Every user_type should have a login method and an approval mode.
  config.user_types.forEach((ut) => {
    const hasLogin = Object.values(config.login_options).some((o) => o.users.includes(ut));
    const hasApproval = Object.values(config.approval_flow_options).some((o) => o.users.includes(ut));
    if (!hasLogin) msgs.push(`"${ut}" has no login method`);
    if (!hasApproval) msgs.push(`"${ut}" has no approval mode`);
  });
  return msgs;
};
